import React, { useEffect, useState } from 'react';
import { setMetaConsent, getFbp, getFbc, ensurePixelInit } from '../../lib/metaPixel';
import axios from 'axios';
import { Bell, Heart } from 'lucide-react';
import { API } from '../../config';
import { requestNotificationPermission, getPushPlatform } from '../../services/pushService';
import { alertsTitle, alertsSubtitle, cleanTeamName, previewText } from './alertsCopy';

const post = (body) => axios.post(`${API}/auth/alerts-popup`, body).catch(() => null);

export const TeamStep = ({ onPick, onSkip, skipLabel = 'Salta', title = 'Qual è la tua squadra del cuore?' }) => {
  const [leagues, setLeagues] = useState([]);
  useEffect(() => {
    axios.get(`${API}/teams/italian`).then((r) => setLeagues(r.data?.leagues || [])).catch(() => {});
  }, []);
  const byId = {};
  leagues.forEach((lg) => lg.teams.forEach((t) => { byId[t.id] = t; }));

  return (
    <>
      <div className="w-14 h-14 rounded-full bg-[#F5A623]/15 border border-[#F5A623]/40 flex items-center justify-center mx-auto mb-4">
        <Heart className="w-7 h-7 text-[#F5A623]" />
      </div>
      <h2 className="text-xl font-bold text-white text-center mb-5" data-testid="alerts-team-title">{title}</h2>
      <select
        defaultValue=""
        onChange={(e) => byId[e.target.value] && onPick(byId[e.target.value])}
        className="w-full h-14 px-4 rounded-xl bg-[#242424] border border-white/15 text-white focus:border-[#F5A623] outline-none"
        data-testid="alerts-team-select"
      >
        <option value="" disabled>{leagues.length ? 'Seleziona la squadra' : 'Caricamento squadre…'}</option>
        {leagues.map((lg) => (
          <optgroup key={lg.league_id} label={lg.name}>
            {lg.teams.map((t) => <option key={t.id} value={t.id}>{cleanTeamName(t.name)}</option>)}
          </optgroup>
        ))}
      </select>
      <button onClick={onSkip} className="w-full mt-3 py-3 text-sm font-bold text-gray-400 hover:text-white transition-colors" data-testid="alerts-team-skip">
        {skipLabel}
      </button>
    </>
  );
};

const NotificationPreview = ({ text }) => (
  <div className="flex items-start gap-3 p-3 mb-6 rounded-2xl bg-white/[0.06] border border-white/10 shadow-lg" data-testid="alerts-notification-preview">
    <img src="/logo192.png" alt="RVincente" className="w-9 h-9 rounded-lg shrink-0" />
    <div className="min-w-0 flex-1">
      <div className="flex items-center justify-between text-[11px] text-gray-400">
        <span className="font-bold text-gray-200">RVincente</span><span>ora</span>
      </div>
      <p className="text-[13px] text-white leading-snug mt-0.5" data-testid="alerts-notification-preview-text">{text}</p>
    </div>
  </div>
);

const MetaConsentBox = ({ checked, onChange }) => (
  <label className="flex items-start gap-3 cursor-pointer mb-5 text-left" data-testid="alerts-meta-consent-label">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => { onChange(e.target.checked); if (e.target.checked) ensurePixelInit(); }}
      className="mt-0.5 w-4 h-4 accent-[#F5A623] shrink-0"
      data-testid="alerts-meta-consent-checkbox"
    />
    <span className="text-xs text-gray-400">
      Aiutaci a far conoscere RVincente: consenti a Meta di misurare le nostre pubblicità.{' '}
      <a href="/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#F5A623] underline">Info</a>
    </span>
  </label>
);

const ConfirmStep = ({ title, subtitle, preview, busy, onYes, onLater, metaBox }) => (
  <>
    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#F5A623] to-[#FF6B6B] flex items-center justify-center mx-auto mb-4">
      <Bell className="w-7 h-7 text-[#1A1A1A]" />
    </div>
    <h2 className="text-lg font-bold text-white text-center leading-snug mb-3" data-testid="alerts-confirm-title">{title}</h2>
    <p className="text-sm text-gray-400 text-center mb-6" data-testid="alerts-confirm-subtitle">{subtitle}</p>
    <NotificationPreview text={preview} />
    {metaBox}
    <div className="grid grid-cols-2 gap-3">
      <button onClick={onLater} disabled={busy} className="h-14 rounded-2xl border border-[#F5A623]/60 bg-[#F5A623]/10 text-[#F5A623] font-bold text-sm disabled:opacity-50" data-testid="alerts-later-btn">
        Più tardi
      </button>
      <button onClick={onYes} disabled={busy} className="h-14 rounded-2xl border border-[#F5A623]/60 bg-[#F5A623]/10 text-[#F5A623] font-bold text-sm disabled:opacity-50" data-testid="alerts-yes-btn">
        Sì, avvisami
      </button>
    </div>
  </>
);

const fetchNextMatch = () => axios.get(`${API}/user/favorite-team/next`).then((r) => r.data?.match || null).catch(() => null);

export const AlertsPopup = ({ needsEmail, needsPush, needsMeta, reconfirm, initialTeam, onDone }) => {
  const [metaChecked, setMetaChecked] = useState(false);
  const [team, setTeam] = useState(initialTeam || null);
  const [match, setMatch] = useState(null);
  const [step, setStep] = useState(initialTeam ? 'loading' : 'team');
  const [busy, setBusy] = useState(false);
  const flags = { needs_email: needsEmail, needs_push: needsPush, needs_meta: !!needsMeta };

  useEffect(() => {
    post({ action: 'shown', ...flags });
    if (initialTeam) fetchNextMatch().then((m) => { setMatch(m); setStep('confirm'); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pickTeam = async (t) => {
    setTeam(t);
    setStep('loading');
    await post({ action: 'team', team: t });
    setMatch(await fetchNextMatch());
    setStep('confirm');
  };

  const title = alertsTitle(team, match);
  const subtitle = alertsSubtitle(needsEmail, needsPush, reconfirm, team);
  const textShown = `${title} ${subtitle}`;

  const handleYes = async () => {
    setBusy(true);
    if (needsMeta && metaChecked) setMetaConsent(true);
    await post({
      action: 'yes', text_shown: textShown, ...flags,
      ...(needsMeta ? { meta_consent: metaChecked, fbp: metaChecked ? getFbp() : null, fbc: metaChecked ? getFbc() : null } : {}),
    });
    if (needsPush) {
      let token = null;
      try { token = await requestNotificationPermission(); } catch { token = null; }
      if (token) {
        await axios.post(`${API}/notifications/register-token`, { token, platform: getPushPlatform() }).catch(() => {});
      }
      await post({ action: token ? 'system_granted' : 'system_denied', ...flags });
    }
    onDone();
  };

  const handleLater = async () => {
    setBusy(true);
    await post({ action: 'later', ...flags });
    onDone();
  };

  return (
    <div className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center bg-black/75 backdrop-blur-sm" data-testid="alerts-popup-overlay">
      <div className="w-full sm:max-w-md bg-gradient-to-b from-[#1F1F1F] to-[#171717] rounded-t-3xl sm:rounded-3xl border-t sm:border border-[#F5A623]/30 p-6 sm:p-7" data-testid="alerts-popup">
        {step === 'team' && <TeamStep onPick={pickTeam} onSkip={() => setStep('confirm')} />}
        {step === 'loading' && (
          <div className="h-48 flex items-center justify-center" data-testid="alerts-loading">
            <div className="w-8 h-8 rounded-full border-2 border-[#F5A623] border-t-transparent animate-spin" />
          </div>
        )}
        {step === 'confirm' && <ConfirmStep title={title} subtitle={subtitle} preview={previewText(team, match)} busy={busy} onYes={handleYes} onLater={handleLater} metaBox={needsMeta ? <MetaConsentBox checked={metaChecked} onChange={setMetaChecked} /> : null} />}
      </div>
    </div>
  );
};

export default AlertsPopup;
